import { useEffect, useRef, useState } from "react";
import { agentsApi } from "api/agents";
import { AvailableAgent } from "types";
import { FaCheck } from "react-icons/fa";
import { BiChevronDown } from "react-icons/bi";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AssignSpecialistsStepProps {
  formData: {
    name: string;
    description: string;
    icon: File | null;
    knowledgeBase: any[];
    workflows: string[];
    integration_ids?: string[];
    agent_ids?: string[];
  };
  setFormData: (data: any) => void;
}

const AssignSpecialistsStep = ({
  formData,
  setFormData,
}: AssignSpecialistsStepProps) => {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<AvailableAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoading(true);
        const response = await agentsApi.getAvailable();
        setAgents(response.items || []);
        setError(null);
      } catch (err) {
        setError(t("agents.assignedSpecialists.failedToFetchSpecialists"));
        setAgents([]); // Ensure agents is always an array even on error
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const selectedIds = formData.agent_ids || [];
  const selectedAgents = agents.filter((agent) =>
    selectedIds.includes(agent.id)
  );
  const selectedCount = selectedIds.length;

  const toggleAgent = (agentId: string) => {
    const newIds = selectedIds.includes(agentId)
      ? selectedIds.filter((id) => id !== agentId)
      : [...selectedIds, agentId];
    setFormData({ ...formData, agent_ids: newIds });
  };

  const removeAgent = (agentId: string) => {
    const newIds = selectedIds.filter((id) => id !== agentId);
    setFormData({ ...formData, agent_ids: newIds });
  };

  const Checkbox = ({
    checked,
    onChange,
  }: {
    checked: boolean;
    onChange: () => void;
  }) => (
    <label className="flex cursor-pointer items-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
          checked
            ? "border-gray-900 bg-gray-900 dark:border-gray-100 dark:bg-gray-100"
            : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
        }`}
      >
        {checked && (
          <FaCheck size={12} className="text-white dark:text-gray-900" />
        )}
      </span>
    </label>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">
          {t("agents.assignedSpecialists.loadingSpecialists")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h2 className="mb-6 text-2xl font-bold">
        {t("agents.assignedSpecialists.assignSpecialists")}
      </h2>

      {/* Dropdown Field */}
      <div className="relative mb-4" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700 shadow-sm transition-all hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        >
          <div className="flex items-center justify-between">
            <span
              className={
                selectedCount > 0
                  ? "text-gray-900 dark:text-gray-100"
                  : "text-gray-500 dark:text-gray-400"
              }
            >
              {selectedCount > 0
                ? t("agents.assignedSpecialists.specialistSelected", {
                    count: selectedCount,
                  })
                : t("agents.assignedSpecialists.selectSpecialists")}
            </span>
            <BiChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </button>

        {/* Dropdown List */}
        {isDropdownOpen && (
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <div className="max-h-60 overflow-y-auto p-2">
              {Array.isArray(agents) &&
                agents.map((agent) => {
                  const isSelected = selectedIds.includes(agent.id);
                  return (
                    <div
                      key={agent.id}
                      onClick={() => toggleAgent(agent.id)}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleAgent(agent.id)}
                      />
                      <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">
                        {agent.name}
                      </span>
                    </div>
                  );
                })}
              {(!Array.isArray(agents) || agents.length === 0) && (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {t("agents.assignedSpecialists.noSpecialistsAvailable")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected Chips */}
      <div className="mb-4 space-y-2">
        {selectedAgents.length > 0 ? (
          selectedAgents.map((agent) => (
            <div
              key={agent.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2.5 dark:border-gray-600 dark:bg-gray-800"
            >
              <span className="text-sm text-gray-700 dark:text-gray-200">
                {agent.name}
              </span>
              <button
                type="button"
                onClick={() => removeAgent(agent.id)}
                className="ml-2 rounded p-1 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                aria-label={`Remove ${agent.name}`}
              >
                <X size={16} />
              </button>
            </div>
          ))
        ) : (
          <div className="flex justify-center py-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t("agents.assignedSpecialists.noSpecialistsSelected")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignSpecialistsStep;
